import React, { useState, useEffect, FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { useQueryClient } from '@tanstack/react-query';

import { IdParamsType } from 'utils/types';
import { PunchupTicketTypeTile } from 'features/common/components/Punchup/composites/tiles/PunchupTicketTypeTile';
import { PunchupDraggableTile } from 'features/common/components/Punchup/composites/tiles/PunchupDraggableTile';
import { ROUTES } from 'features/routes';
import { TicketTypeProps, TicketTypesResponse } from '../types';
import { TicketTypesListProps } from './types';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { formatCompactStatus } from '../utils';
import { reorderTicketTypesPut } from '../services';

/**
 * Reorder the cached RAW ticket-type rows to match the new on-screen order and re-stamp `orderIndex`
 * (the field `sortTicketTypes` orders by), so the section's re-sort preserves the new order after the
 * optimistic cache write. Keyed by `ID` (the raw response key) against the camelized list's `iD`.
 * Falls back to the untouched cache on any id mismatch rather than dropping rows.
 */
const reorderCachedTicketTypes = (
	raw: TicketTypesResponse[] | undefined,
	newOrder: TicketTypeProps[]
): TicketTypesResponse[] | undefined => {
	if (!raw) return raw;
	const byId = new Map(raw.map((row) => [row.ID, row]));
	const reordered = newOrder
		.map((t, idx) => {
			const row = byId.get(t.iD);
			return row ? { ...row, orderIndex: idx } : undefined;
		})
		.filter((row): row is TicketTypesResponse => row !== undefined);
	return reordered.length === raw.length ? reordered : raw;
};

const TicketTypeGrid: FC<TicketTypesListProps> = ({ ticketTypes, onEdit }) => {
	const { id: eventId } = useParams<IdParamsType>();
	const { event } = useGetEvent();
	const timezone = event?.timeZone || 'UTC';
	// Tile colour rail — the ticket type's colour code (resolved by name from the shared source).
	const { resolveByName } = useTicketTypeColors();
	const { push } = useHistory();
	const queryClient = useQueryClient();

	const [orderedTypes, setOrderedTypes] = useState<TicketTypeProps[]>(ticketTypes);

	useEffect(() => {
		setOrderedTypes(ticketTypes);
	}, [ticketTypes]);

	const onClickDetails = (ticketId: number) => {
		if (onEdit) {
			onEdit(ticketId);
		} else {
			push(ROUTES.TICKET_DETAILS_PATH.replace(':id', eventId).replace(':ticketId', ticketId.toString()));
		}
	};

	const handleDragEnd = async (result: DropResult) => {
		if (!result.destination || result.destination.index === result.source.index) return;

		const reordered = [...orderedTypes];
		const [moved] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, moved);
		const withNewOrder = reordered.map((t, idx) => ({ ...t, orderIndex: idx }));

		// Optimistic: move the local list (smooth drop) AND the React Query cache in lock-step, so no
		// intermediate render or refetch can snap the row back to the old order while the PUT is in
		// flight. We deliberately DON'T invalidate on success — the cache already holds the persisted
		// order, and a refetch here can read before the write is visible, which is exactly the
		// revert-then-correct flicker we're fixing. The next natural load reconciles. Roll both back on error.
		const previous = orderedTypes;
		const previousCache = queryClient.getQueryData<TicketTypesResponse[]>(['ticketTypes', eventId]);
		setOrderedTypes(withNewOrder);
		queryClient.setQueryData<TicketTypesResponse[]>(['ticketTypes', eventId], (raw) =>
			reorderCachedTicketTypes(raw, withNewOrder)
		);
		try {
			await reorderTicketTypesPut(withNewOrder);
		} catch {
			setOrderedTypes(previous);
			queryClient.setQueryData(['ticketTypes', eventId], previousCache);
		}
	};

	const renderTile = ({
		iD,
		initialPrice,
		sold,
		holdsAmount,
		name,
		sellingTime,
		offSellingTime,
		ticketVisibility,
		payWhatYouWant,
		minPrice,
		maxPrice,
	}: TicketTypeProps) => {
		const statusTitle = formatCompactStatus(sellingTime, offSellingTime, timezone, ticketVisibility ?? true);
		return (
			<PunchupTicketTypeTile
				status={statusTitle}
				name={name}
				initialPrice={initialPrice}
				isPayWhatYouWant={payWhatYouWant}
				minPrice={minPrice}
				maxPrice={maxPrice}
				descriptions={[`${sold} sold · ${holdsAmount} comped`]}
				color={resolveByName(name)}
				onClick={() => onClickDetails(iD)}
				dataCy={`ticket-tile-${iD}`}
			/>
		);
	};

	if (onEdit) {
		return (
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="ticket-types">
					{(provided) => (
						<div
							ref={provided.innerRef}
							{...provided.droppableProps}
							data-cy="tickets-section"
							className="punchupDraggableList"
						>
							{orderedTypes.map((type, i) => (
								<PunchupDraggableTile key={type.iD} id={String(type.iD)} index={i}>
									{renderTile(type)}
								</PunchupDraggableTile>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		);
	}

	return (
		<>
			{ticketTypes.map((type) => (
				<React.Fragment key={type.iD}>{renderTile(type)}</React.Fragment>
			))}
		</>
	);
};

export { TicketTypeGrid };
